import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Award, Users, ShieldCheck, Headphones, Briefcase, Globe, ThumbsUp,
  Code2, Smartphone, PenTool, Cloud, Cpu, Webhook, TrendingUp,
  ClipboardList, FileSearch, Rocket, Phone,
  ArrowRight, ChevronRight,
} from 'lucide-react';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

const HERO_IMAGE = {
  desktopWidth: '750px',
  desktopHeight: 'auto',
  offsetX: '0px',
  offsetY: '0px',
};

/* ============================== DATA ============================== */

const FEATURES = [
  { Icon: Award, top: 'Quality', bottom: 'Assured' },
  { Icon: Users, top: 'Expert', bottom: 'Team' },
  { Icon: ShieldCheck, top: 'Secure', bottom: '& Reliable' },
  { Icon: Headphones, top: '24/7', bottom: 'Support' },
];

const STATS = [
  { Icon: Briefcase, value: '100+', label: 'Successful Projects' },
  { Icon: Users, value: '50+', label: 'Skilled Professionals' },
  { Icon: Globe, value: '10+', label: 'Industries Served' },
  { Icon: ThumbsUp, value: '99%', label: 'Client Satisfaction' },
  { Icon: Headphones, value: '24/7', label: 'Support & Maintenance' },
];

const CORE = [
  { 
    Icon: Code2, color: '#3284c7', title: 'Software Development', 
    desc: 'Custom software solutions to streamline operations and accelerate growth.',
    features: ['ERP Development', 'CRM Development', 'Web Applications', 'Enterprise Solutions'],
    link: '/softwaredevelopment'
  },
  { 
    Icon: Smartphone, color: '#16a34a', title: 'Mobile App Development', 
    desc: 'High-performance mobile apps for Android, iOS and cross-platform.',
    features: ['Android App Development', 'iOS App Development', 'Flutter Development', 'React Native Development'],
    link: '/mobileappdevelopment'
  },
  { 
    Icon: Globe, color: '#2563eb', title: 'Web Development', 
    desc: 'Modern, responsive and scalable websites and web applications.',
    features: ['Business Websites', 'E-Commerce Solutions', 'Portal Development', 'Progressive Web Apps'],
    link: '/webdevelopment'
  },
  { 
    Icon: PenTool, color: '#f29132', title: 'UI/UX Design', 
    desc: 'Beautiful, intuitive and user-centered designs that engage users.',
    features: ['Wireframing', 'Prototyping', 'Dashboard Design', 'User Experience Research'],
    link: '/uiuxdesign'
  },
  { 
    Icon: Cloud, color: '#0ea5e9', title: 'Cloud Solutions', 
    desc: 'Scalable, secure and cost-effective cloud solutions for your business.',
    features: ['AWS Solutions', 'Microsoft Azure', 'Google Cloud Platform', 'Cloud Migration'],
    link: '/cloudsolutions'
  },
  { 
    Icon: Cpu, color: '#16a34a', title: 'AI & Automation', 
    desc: 'AI-powered solutions to automate processes and drive intelligence.',
    features: ['AI Chatbots', 'AI Analytics', 'OCR & Document Intelligence', 'RPA Automation'],
    link: '/aiautomation'
  },
  { 
    Icon: Webhook, color: '#e11d48', title: 'API Development', 
    desc: 'Powerful and secure APIs to integrate your systems and services.',
    features: ['RESTful APIs', 'Third-party Integrations', 'Payment Gateway Integration', 'SMS / Email / WhatsApp APIs'],
    link: '/apidevelopment'
  },
  { 
    Icon: TrendingUp, color: '#f59e0b', title: 'Digital Transformation', 
    desc: 'Transform your business with digital strategies and modern technologies.',
    features: ['Process Automation', 'Legacy System Migration', 'Digital Consulting', 'Technology Modernization'],
    link: '/digitaltransformation'
  },
];

const PROCESS = [
  { Icon: ClipboardList, num: '01', title: 'Requirement Gathering', desc: 'Understanding your business goals and requirements.' },
  { Icon: FileSearch, num: '02', title: 'Business Analysis', desc: 'Analyzing requirements and creating a clear project roadmap.' },
  { Icon: PenTool, num: '03', title: 'UI/UX Design', desc: 'Designing intuitive interfaces and amazing user experiences.' },
  { Icon: Code2, num: '04', title: 'Development', desc: 'Building scalable, secure and high-performance solutions.' },
  { Icon: ShieldCheck, num: '05', title: 'Testing', desc: 'Rigorous testing to ensure quality, security and performance.' },
  { Icon: Rocket, num: '06', title: 'Deployment', desc: 'Seamless deployment and go-live with full support.' },
  { Icon: Headphones, num: '07', title: 'Support & Maintenance', desc: 'Continuous support and maintenance for long-term success.' },
];

/* ============================== HELPERS ============================== */

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

function SectionHead({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center mb-12">
      <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">{eyebrow}</span>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>{title}</h2>
      {subtitle && <p className="mt-3 text-slate-500">{subtitle}</p>}
    </div>
  );
}

/* ============================== PAGE ============================== */

const Services = () => {
  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @media (min-width: 1024px) {
          .se-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 600px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-8 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="se-fade-up">
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-5">
                <Link to="/" className="hover:text-primary">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Services</span>
              </nav>

              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>
                Our Services
              </h1>
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                Delivering end-to-end software development services that transform ideas into innovative digital solutions, enabling businesses to innovate, automate, scale, and succeed in an ever-evolving digital world.
              </p>

              <div className="grid grid-cols-4 gap-3 mt-8 max-w-lg">
                {FEATURES.map((f) => (
                  <div key={f.bottom} className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-2">
                      <f.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-bold text-slate-800 text-sm leading-tight">{f.top}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{f.bottom}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden sm:block se-fade-up relative lg:min-h-[440px]" style={{ animationDelay: '.15s' }}>
              <img
                src="/Service_Img.png"
                alt="SmartEdz services preview"
                className="se-hero-img w-full h-auto max-w-[600px] mx-auto block select-none drop-shadow-2xl"
                draggable="false"
                style={{
                  '--hero-w': HERO_IMAGE.desktopWidth,
                  '--hero-h': HERO_IMAGE.desktopHeight,
                  '--hero-x': HERO_IMAGE.offsetX,
                  '--hero-y': HERO_IMAGE.offsetY,
                }}
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= STATS BAR ================= */}
      <section className={`${CONTAINER} relative z-20 -mt-4`}>
        <Reveal className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-lg px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-6 divide-y md:divide-y-0 lg:divide-x divide-slate-100">
            {STATS.map((s) => (
              <div key={s.label} className="flex items-center gap-3 justify-center px-2">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <s.Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-xl font-extrabold text-primary leading-none">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= CORE SERVICES ================= */}
      <section className={`${CONTAINER} py-16`}>
        <Reveal><SectionHead eyebrow="What We Offer" title="Our Core Services" subtitle="Comprehensive services tailored to meet your business needs" /></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CORE.map((c, i) => (
            <Reveal key={c.title} delay={(i % 4) * 60}>
              <div className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: `${c.color}1A` }}>
                    <c.Icon className="w-6 h-6" style={{ color: c.color }} />
                  </div>
                  <h3 className="font-bold text-slate-800 text-[15px] leading-tight">{c.title}</h3>
                </div>
                <p className="text-[13px] text-slate-500 leading-relaxed mb-4">{c.desc}</p>
                <ul className="space-y-2 flex-1">
                  {c.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-slate-500">
                      <span className="mt-[7px] w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to={c.link} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= DEVELOPMENT PROCESS ================= */}
      <section className="bg-[#f5f9fd] py-16">
        <div className={CONTAINER}>
          <Reveal><SectionHead eyebrow="Our Approach" title="Our Development Process" /></Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-8">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 60}>
                <div className="relative text-center xl:after:content-[''] xl:after:absolute xl:after:top-8 xl:after:left-[62%] xl:after:w-[76%] xl:after:border-t-2 xl:after:border-dashed xl:after:border-slate-300 xl:last:after:hidden">
                  <div className="relative z-10 w-16 h-16 mx-auto rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-3">
                    <p.Icon className="w-7 h-7 text-primary" />
                  </div>
                  <div className="text-xs font-bold text-primary mb-1">{p.num}</div>
                  <h3 className="font-bold text-slate-800 text-sm mb-1.5">{p.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed max-w-[180px] mx-auto">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA BANNER ================= */}
      <section className={`${CONTAINER} py-14`}>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0A1B3D] via-[#0f2e6b] to-[#123a86] px-6 sm:px-10 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5 text-center md:text-left">
              <div className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center shrink-0">
                <Rocket className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-extrabold text-white">Have a Project in Mind?</h3>
                <p className="text-slate-300 mt-1">Let's build something amazing together.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-primary bg-white hover:bg-slate-100 transition whitespace-nowrap">
                Start Your Project <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white ring-1 ring-white/40 hover:bg-white/10 transition whitespace-nowrap">
                <Phone className="w-4 h-4" /> Schedule a Call
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;